import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { Course, CourseStatusAttemptEnum } from '@/client/courses';
import { ScormVersion } from '@/client/learner-course';
import { User } from '@/client/users';
import {
  ContentFrameIframe,
  ContentFrameNextButton,
  PreviewAspectRatioFrame,
} from '@/components/courses/content-frame.styles';
import { CourseModuleFrame } from '@/components/courses/CourseModuleFrame';
import { ScormProvider } from '@/components/courses/scorm';
import { useScormDispatchUrl } from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { AppSpinner } from '@/ui/app-spinner';
import { FlexContainer } from '@/ui/styled-ui';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledLoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type ScormModulePreviewProps = {
  course: Course;
  resourceId?: string;
  selectedLanguage: LanguagesEnum;
  currentUser: User;
  isLastModule: boolean;
  onNextModule: () => void;
};

export const ScormModulePreview: React.FC<ScormModulePreviewProps> = ({
  course,
  resourceId,
  selectedLanguage,
  currentUser,
  isLastModule,
  onNextModule,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [status, setStatus] = useState<CourseStatusAttemptEnum>();
  const [urlAccessible, setUrlAccessible] = useState(true);

  const { scormDetails, isLoading, isError } = useScormDispatchUrl({
    courseId: course.id,
    resourceId,
    language: selectedLanguage,
    onError: (e) => handleAxiosError(e as Error | AxiosError, toast),
  });

  useEffect(() => {
    if (!scormDetails?.url) return;

    try {
      new URL(scormDetails.url);
    } catch {
      setUrlAccessible(false);
      return;
    }

    setUrlAccessible(true);

    fetch(scormDetails.url, { method: 'GET' })
      .then((res) => {
        if (!res.ok) setUrlAccessible(false);
      })
      .catch(() => setUrlAccessible(false));
  }, [scormDetails?.url]);

  if (isError || !urlAccessible) {
    return (
      <CourseModuleFrame direction="column">
        <FlexContainer
          direction="column"
          align="center"
          justify="center"
          style={{ flex: 1 }}
        >
          <p>{t('content.preview.scorm.unavailable')}</p>
        </FlexContainer>
      </CourseModuleFrame>
    );
  }

  return (
    <CourseModuleFrame direction="column">
      {isLoading || !scormDetails ? (
        <StyledLoadingContainer>
          <AppSpinner />
        </StyledLoadingContainer>
      ) : (
        <PreviewAspectRatioFrame>
          <ScormProvider
            course={course}
            version={scormDetails.version as ScormVersion}
            user={currentUser}
            isPreview={true}
            setStatus={setStatus}
            isLastModule={isLastModule}
            setNextModule={onNextModule}
          >
            <ContentFrameIframe src={scormDetails.url} />
          </ScormProvider>
          {status === CourseStatusAttemptEnum.COMPLETED && !isLastModule && (
            <ContentFrameNextButton
              size="xs"
              icon="pi pi-chevron-right"
              onClick={onNextModule}
            />
          )}
        </PreviewAspectRatioFrame>
      )}
    </CourseModuleFrame>
  );
};
